import { useQuery } from "@tanstack/react-query";
import { withAppHOC } from "../../hoc";
import styled from "styled-components";
import PageHeader from "../../components/PageHeader/PageHeader";
import { StyledContentContainer } from "../../components/styled";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import { MessageBox } from "../../components/FormElements";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMobileAlt,
  faDesktop,
  faTabletAlt,
  faPowerOff,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import NoData from "../../components/NoData/NoData";

const StyledGridLayout = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const StyledSessionCard = styled.section`
  display: flex;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  background: #fff;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }
`;

const StatusBar = styled.span`
  width: 6px;
  background: ${({ status }) => (status === "Active" ? "#00ff85" : "#ff4d4f")};
`;

const SessionContent = styled.section`
  flex: 1;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
`;

const SessionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 15px;

  svg {
    font-size: 22px;
  }
`;

const SessionDetails = styled.div`
  margin-top: 10px;
  font-size: 14px;
  line-height: 1.5;

  p {
    margin: 3px 0;
  }

  span {
    font-weight: 500;
    color: #555;
  }
`;

const LogoutButton = styled.button`
  align-self: flex-end;
  margin-top: 10px;
  padding: 6px 10px;
  background: #ff4d4f;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #ff7875;
  }

  svg {
    font-size: 12px;
  }
`;

const getDeviceIcon = (deviceType) => {
  if (!deviceType) return faDesktop;
  const type = deviceType.toLowerCase();
  if (type.includes("mobile")) return faMobileAlt;
  if (type.includes("tablet")) return faTabletAlt;
  return faDesktop;
};

const Sessions = () => {
  const fetchData = async () => {
    const response = await API.get(`/authentication/sessions`);
    return response.data;
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["sessions"],
    queryFn: fetchData,
  });

  const reloadData = async () => await refetch();

  const renderPageHeading = () => <PageHeader title="Your Active Sessions" />;

  const handleLogout = async (sessionToken) => {
    await API.delete(`/authentication/logout?token=${sessionToken}`);
    reloadData();
  };

  if (isLoading || isRefetching) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  if (!data?.data || data?.data.length === 0) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <NoData />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      {renderPageHeading()}
      <StyledContentContainer>
        <StyledGridLayout>
          {data.data.map((session, index) => (
            <StyledSessionCard key={index}>
              <StatusBar status={session.status} />
              <SessionContent>
                <SessionHeader>
                  <FontAwesomeIcon icon={getDeviceIcon(session.deviceType)} />
                  {session.deviceType || "Desktop"}
                </SessionHeader>
                <SessionDetails>
                  <p>
                    <span>Status:</span> {session.status || "Active"}
                  </p>
                  <p>
                    <span>IP:</span> {session.ipAddress || "N/A"}
                  </p>
                  <p>
                    <span>Last Active:</span>{" "}
                    {moment(session.lastActive).fromNow()}
                  </p>
                  {session.location && (
                    <p>
                      <span>Location:</span> {session.location}
                    </p>
                  )}
                </SessionDetails>
                <LogoutButton onClick={() => handleLogout(session.sessionToken)}>
                  <FontAwesomeIcon icon={faPowerOff} /> Logout
                </LogoutButton>
              </SessionContent>
            </StyledSessionCard>
          ))}
        </StyledGridLayout>
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(Sessions);
